import { useScope } from "@state/scopeContext.lib";
import { useCallback, useMemo } from "react";
import { ZodObject, ZodRawShape, ZodSchema } from "zod";
import { useUserStatus } from "./useUserStatus";

export enum WORKSPACE_USER_CACHE_KEYS {
    RULES_SUGGESTION_ENABLED = "transactions-rules-suggestion-enabled",
    LAST_ENTITY_CACHE_KEY = "accounting-page-last-entity",
    RECONCILIATION_ENTITIES_CACHE_KEY = "reconciliation-page-selected-entities",
    LAST_DATE_PERIOD_CACHE_KEY = "accounting-page-last-date-period",
    LAST_LEDGER_BASIS_CACHE_KEY = "accounting-page-last-ledger-basis",
    ACCOUNTS_CACHE_KEY = "activity-page-selected-accounts",
    TRANSACTIONS_PAGE_VIEW_CACHE_KEY = "transactions-page-base-view-cache",
    ACCRUAL_REVIEW_PAGE_VIEW_CACHE_KEY = "accrual-review-page-view-cache",
    ACCRUAL_REVIEW_PAGE_BASE_VIEW_ID_CACHE_KEY = "accrual-review-page-base-view-id-cache",
    TRANSACTIONS_PAGE_BASE_VIEW_ID_CACHE_KEY = "transactions-page-base-view-id-cache",
    SCHEDULES_REPORT_PAGE_BASE_VIEW_ID_CACHE_KEY = "schedules-report-page-base-view-id-cache",
    SCHEDULES_REPORT_PAGE_VIEW_OPTIONS_CACHE_KEY = "schedules-report-page-view-options-cache",
    SCHEDULES_REPORT_PAGE_VIEW_COLUMNS_CACHE_KEY = "schedules-report-page-view-columns-cache",
    TRANSACTIONS_PAGE_SORTING_CACHE_KEY = "transactions-page-sorting-cache",
    INVOICES_BY_CUSTOMER_PAGE_BASE_VIEW_ID_CACHE_KEY = "invoices-by-customer-page-base-view-id-cache",
    INVOICES_PAGE_FILTERS_CACHE_KEY = "invoices-page-filters-cache",
    INVOICES_PAGE_SORTING_CACHE_KEY = "invoices-page-sorting-cache",
    INVOICES_BY_COUNTERPARTY_PAGE_SORTING_CACHE_KEY = "invoices-by-counterparty-page-sorting-cache",
    INVOICES_PAGE_VIEW_OPTIONS_CACHE_KEY = "invoices-page-view-options-cache",
    INVOICES_PAGE_INVOICE_VIEW_CACHE_KEY = "invoices-page-invoice-view-cache",
    INVOICES_PAGE_LINE_ITEMS_VIEW_CACHE_KEY = "invoices-page-line-items-view-cache",
    INVOICES_PAGE_BY_CUSTOMER_VIEW_CACHE_KEY = "invoices-page-by-customer-view-cache",
    INVOICES_PAGE_INVOICE_BASE_VIEW_ID_CACHE_KEY = "invoices-page-invoice-base-view-id-cache",
    INVOICES_PAGE_LINE_ITEMS_BASE_VIEW_ID_CACHE_KEY = "invoices-page-line-items-base-view-id-cache",
    INVOICES_PAGE_BY_CUSTOMER_BASE_VIEW_ID_CACHE_KEY = "invoices-page-by-customer-base-view-id-cache",
    BILLS_PAGE_FILTERS_CACHE_KEY = "bills-page-filters-cache",
    BILLS_PAGE_SORTING_CACHE_KEY = "bills-page-sorting-cache",
    BILLS_BY_COUNTERPARTY_PAGE_SORTING_CACHE_KEY = "bills-by-counterparty-page-sorting-cache",
    BILLS_PAGE_VIEW_OPTIONS_CACHE_KEY = "bills-page-view-options-cache",
    BILLS_PAGE_BILL_VIEW_CACHE_KEY = "bills-page-bill-view-cache",
    BILLS_PAGE_LINE_ITEMS_VIEW_CACHE_KEY = "bills-page-line-items-view-cache",
    BILLS_PAGE_BY_VENDOR_VIEW_CACHE_KEY = "bills-page-by-vendor-view-cache",
    BILLS_PAGE_BILL_BASE_VIEW_ID_CACHE_KEY = "bills-page-bill-base-view-id-cache",
    BILLS_PAGE_LINE_ITEMS_BASE_VIEW_ID_CACHE_KEY = "bills-page-line-items-base-view-id-cache",
    BILLS_PAGE_BY_VENDOR_BASE_VIEW_ID_CACHE_KEY = "bills-page-by-vendor-base-view-id-cache",
    CASHFLOW_PAGE_FILTERS_CACHE_KEY = "cashflow-page-filters-cache",
    FINANCIAL_DOCUMENTS_PAGE_FILTERS_CACHE_KEY = "financial-documents-page-filters-cache",
    RULES_PAGE_FILTERS_CACHE_KEY = "rules-page-filters-cache",
    CASHFLOW_DRILLDOWN_CACHE_KEY = "cashflow-drilldown-cache",
    DIRECTORY_FILTERS_CACHE_KEY = "directory-filters-cache",
    DISMISSED_CTAS = "dismissed-ctas",
    TASKS_PAGE_FILTERS_CACHE_KEY = "tasks-page-filters-cache",
    TASKS_PAGE_VIEW_CACHE_KEY = "tasks-page-view-cache",
    TASKS_PAGE_DETAIL_VIEW_CACHE_KEY = "tasks-page-detail-view-cache",
    TASKS_PAGE_BASE_VIEW_ID_CACHE_KEY = "tasks-page-base-view-id-cache",
    TASKS_PAGE_BOARD_CACHE_KEY = "tasks-page-board-cache",
    TRANSACTIONS_MATCHING_VIEW_CACHE_KEY = "transactions-matching-view-cache",
    GENERAL_LEDGER_CASH_TRANSACTIONS_DRILLDOWN_VIEW_CACHE_KEY = "general-ledger-cash-transaction-drilldown-view-cache",
    GENERAL_LEDGER_ACCRUAL_TRANSACTIONS_DRILLDOWN_VIEW_CACHE_KEY = "general-ledger-accrual-transaction-drilldown-view-cache",
    COUNTERPARTY_GL_CASH_TRANSACTIONS_DRILLDOWN_VIEW_CACHE_KEY = "counterparty-gl-cash-transaction-drilldown-view-cache",
    COUNTERPARTY_GL_ACCRUAL_TRANSACTIONS_DRILLDOWN_VIEW_CACHE_KEY = "counterparty-gl-accrual-transaction-drilldown-view-cache",
    GENERAL_LEDGER_PAGE_VIEW_CACHE_KEY = "general-ledger-page-view-cache",
    CHECK_DETAIL_PAGE_VIEW_CACHE_KEY = "check-detail-page-view-cache",
    FORM_1099_NEC_PAGE_VIEW_CACHE_KEY = "form-1099-nec-page-view-cache",
    FORM_1099_NEC_PAGE_BASE_VIEW_ID_CACHE_KEY = "form-1099-nec-page-base-view-id-cache",
    PROFIT_LOSS_PAGE_BASE_VIEW_ID_CACHE_KEY = "profit-loss-page-base-view-id-cache",
    BALANCE_SHEET_PAGE_BASE_VIEW_ID_CACHE_KEY = "balance-sheet-page-base-view-id-cache",
    GENERAL_LEDGER_PAGE_BASE_VIEW_ID_CACHE_KEY = "general-ledger-page-base-view-id-cache",
    TRIAL_BALANCE_PAGE_BASE_VIEW_ID_CACHE_KEY = "trial-balance-page-base-view-id-cache",
    CASH_FLOW_STATEMENT_PAGE_BASE_VIEW_ID_CACHE_KEY = "cash-flow-statement-page-base-view-id-cache",
    EXPENSE_BY_VENDOR_PAGE_BASE_VIEW_ID_CACHE_KEY = "expense-by-vendor-page-base-view-id-cache",
    CHART_OF_ACCOUNTS_PAGE_VIEW_CACHE_KEY = "chart-of-accounts-page-view-cache",
    CHART_OF_ACCOUNTS_PAGE_BASE_VIEW_ID_CACHE_KEY = "chart-of-accounts-page-base-view-id-cache",
    CHART_OF_ACCOUNTS_PAGE_FILTERS_CACHE_KEY = "chart-of-accounts-page-filters-cache",
    ACCOUNTS_PAGE_VIEW_CACHE_KEY = "accounts-page-view-cache",
    HISTORY_PAGE_FILTERS_CACHE_KEY = "history-page-filters-cache",
    ACTIVITY_LAST_VISIT_CACHE_KEY = "activity-last-visit-cache",
    ACTIVITY_CHART_DENSITY_CACHE_KEY = "activity-chart-density",
    FINANCIAL_DOCUMENTS_PAGE_VIEW_CACHE_KEY = "financial-documents-page-view-cache",
    FINANCIAL_DOCUMENT_SELECT_IMPORTER_VIEW_CACHE_KEY = "financial-document-select-importer-view-cache",
    FINANCIAL_DOCUMENT_SELECT_ATTACHMENT_VIEW_CACHE_KEY = "financial-document-select-attachment-view-cache",
    FINANCIAL_DOCUMENT_SELECT_MATCHING_VIEW_CACHE_KEY = "financial-document-select-matching-view-cache",
    FINANCIAL_DOCUMENT_SELECT_COUNTERPARTY_VIEW_CACHE_KEY = "financial-document-select-counterparty-view-cache",
    DOCUMENT_CATEGORIES_PAGE_VIEW_CACHE_KEY = "document-categories-page-view-cache",
    TRANSACTION_CATEGORIES_PAGE_VIEW_CACHE_KEY = "transaction-categories-page-view-cache",
    CLASSES_PAGE_VIEW_CACHE_KEY = "classes-page-view-cache",
    ITEMS_PAGE_VIEW_CACHE_KEY = "items-page-view-cache",
    INVOICE_TEMPLATES_PAGE_VIEW_CACHE_KEY = "invoice-templates-page-view-cache",
    ACCRUAL_IMPORTER_INVOICES_PAGE_VIEW_CACHE_KEY = "accrual-importer-invoices-page-view-cache-v2",
    ACCRUAL_IMPORTER_BILLS_PAGE_VIEW_CACHE_KEY = "accrual-importer-bills-page-view-cache-v2",
    ACCRUAL_IMPORTER_INVOICES_PAGE_BASE_VIEW_ID_CACHE_KEY = "accrual-importer-invoices-page-base-view-id-cache",
    ACCRUAL_IMPORTER_BILLS_PAGE_BASE_VIEW_ID_CACHE_KEY = "accrual-importer-bills-page-base-view-id-cache",
    CLOSE_TEMPLATES_PAGE_VIEW_CACHE_KEY = "close-templates-page-view-cache",
    ORGANIZATION_TASK_TEMPLATES_PAGE_VIEW_CACHE_KEY = "organization-task-templates-page-view-cache",
    ORGANIZATION_TASK_TEMPLATES_SERVICE_TEMPLATES_COLUMN_OFFERED_CACHE_KEY = "organization-task-templates-service-templates-column-offered",
    CHART_OF_ACCOUNTS_TEMPLATES_PAGE_VIEW_CACHE_KEY = "chart-of-accounts-templates-page-view-cache",
    REPORT_TEMPLATES_PAGE_VIEW_CACHE_KEY = "report-templates-page-view-cache",
    AGENT_SKILLS_PAGE_VIEW_CACHE_KEY = "agent-skills-page-view-cache",
    AGENT_SKILLS_LIST_COLUMNS_OFFERED_CACHE_KEY = "agent-skills-list-columns-offered",
    SERVICE_TEMPLATE_SCHEDULES_PAGE_VIEW_CACHE_KEY = "service-template-schedules-page-view-cache",
    CLOSE_TEMPLATE_TASKS_DRAWER_VIEW_CACHE_KEY = "close-template-tasks-drawer-view-cache",
    CLOSE_TEMPLATE_APPLICATIONS_DRAWER_VIEW_CACHE_KEY = "close-template-applications-drawer-view-cache",
    SCHEDULE_APPLICATIONS_DRAWER_VIEW_CACHE_KEY = "schedule-applications-drawer-view-cache",
    SCHEDULE_APPLICATIONS_PAGE_VIEW_CACHE_KEY = "schedule-applications-page-view-cache",
    SCHEDULES_PAGE_VIEW_CACHE_KEY = "schedules-page-view-cache",
    CLIENTS_CLOSE_OVERVIEW_PAGE_VIEW_CACHE_KEY = "clients-close-overview-page-view-cache",
    CLIENTS_WORK_OVERVIEW_PAGE_VIEW_CACHE_KEY = "clients-work-overview-page-view-cache",
    COUNTERPARTY_DIRECTORY_PAGE_VIEW_CACHE_KEY = "counterparty-directory-page-view-cache",
    COUNTERPARTY_DIRECTORY_BASE_VIEW_ID_CACHE_KEY = "counterparty-directory-base-view-id-cache",
    CLOSE_TEMPLATE_APPLY_PREVIEW_VIEW_CACHE_KEY = "close-template-apply-preview-view-cache-v2",
    ACCRUAL_MATCH_DRAWER_VIEW_CACHE_KEY = "accrual-match-drawer-view-cache-v3",
    // Distinct from the key above (PRO-12048): the two drawers' default
    // column sets diverged (Category/Cash ledger/Accrual ledger vs a single
    // basis-swapped Account column), so sharing one cache key would let a
    // view cached from one drawer shape leak into the other's ViewTable
    // (mergeCachedView only filters by "known to createAccrualMatchViewConfig",
    // which includes both shapes' columns).
    ACCRUAL_MATCH_DRAWER_WORKSPACE_VIEW_CACHE_KEY = "accrual-match-drawer-workspace-view-cache",
    ACCRUAL_MATCH_DRAWER_SCHEDULE_COLUMN_OFFERED_CACHE_KEY = "accrual-match-drawer-schedule-column-offered",
    ACCRUAL_MATCH_DRAWER_WORKSPACE_SCHEDULE_COLUMN_OFFERED_CACHE_KEY = "accrual-match-drawer-workspace-schedule-column-offered",
    ACCRUAL_MATCH_DRAWER_WORKSPACE_CASH_COLUMN_OFFERED_CACHE_KEY = "accrual-match-drawer-workspace-cash-column-offered",
    ACCRUAL_MATCH_DRAWER_WORKSPACE_ACCRUAL_COLUMN_OFFERED_CACHE_KEY = "accrual-match-drawer-workspace-accrual-column-offered",
    // v2: allocation columns now follow the find-match set (Category / Cash
    // ledger / Accrual ledger) instead of the legacy Account + Class defaults.
    ACCRUAL_ALLOCATION_DRAWER_VIEW_CACHE_KEY = "accrual-allocation-drawer-view-cache-v2",
    ACCRUAL_ALLOCATION_DRAWER_CASH_COLUMN_OFFERED_CACHE_KEY = "accrual-allocation-drawer-cash-column-offered",
    ACCRUAL_ALLOCATION_DRAWER_ACCRUAL_COLUMN_OFFERED_CACHE_KEY = "accrual-allocation-drawer-accrual-column-offered",
    ACCOUNTING_MIGRATIONS_VIEW_CACHE_KEY = "accounting-migrations-view-cache",
}

/**
 * This cache is workspace scoped, and is cleared on every login and logout.
 * WARNING: This hook only reads value from localStorage on mount, it won't refresh for already mounted component.
 * @param key Cache key that will be used to create the final key which contains the workspaceid and the user id as well
 * @param zodSchema If specified, the data returned from the cache is going to be parsed. When parsing fails, undefined is returned
 * @returns
 */
export function useWorkspaceUserCache<T>(
    key?: WORKSPACE_USER_CACHE_KEYS,
    zodSchema?: ZodSchema<T> | ZodObject<ZodRawShape>,
): [T | undefined, (value: T | undefined) => void] {
    const { activeWorkspaceKey } = useScope();
    const { data: userStatus } = useUserStatus();
    const userId = userStatus?.user.id;
    const finalKey = `${activeWorkspaceKey}/${key}-${userId}`;

    const value = useMemo<T>(() => {
        if (!key || userId === undefined) {
            return undefined;
        }

        let rawValue: string | null;
        try {
            rawValue = localStorage.getItem(finalKey);
        } catch {
            return undefined;
        }

        if (!rawValue) {
            return undefined;
        }

        let parsedValue;
        try {
            parsedValue = JSON.parse(rawValue);
        } catch {
            return undefined;
        }

        if (zodSchema) {
            if (zodSchema.safeParse(parsedValue).success) {
                parsedValue = zodSchema.parse(parsedValue);
            } else {
                parsedValue = undefined;
            }
        }

        return parsedValue;
    }, [finalKey, zodSchema, key, userId]);

    const setter = useCallback(
        (v: T | undefined) => {
            if (!key || userId === undefined) {
                return;
            }
            try {
                if (v === undefined) {
                    localStorage.removeItem(finalKey);
                } else {
                    localStorage.setItem(finalKey, JSON.stringify(v));
                }
            } catch {
                return;
            }
        },
        [finalKey, key, userId],
    );

    return [value, setter];
}
